(() => {
  if (window.__awSearchInitialized) {
    return;
  }

  window.__awSearchInitialized = true;

  const instances = new Map();
  const WISHLIST_STORAGE_KEY = "aw-collection-wishlist-state";
  const WISHLIST_SYNC_EVENT = "aw:collection-product-card-wishlist-sync";

  class AWSearch {
    constructor(root) {
      this.root = root;
      this.collectionLinksWrap = this.root.querySelector(".aw-search__collection-links-wrap");
      this.collectionLinksViewport = this.root.querySelector("[data-aw-collection-links]");
      this.collectionLinksScrollbar = this.root.querySelector("[data-aw-collection-links-scrollbar]");
      this.collectionLinksScrollbarThumb = this.root.querySelector("[data-aw-collection-links-scrollbar-thumb]");
      this.wishlistIconDefault = this.root.dataset.wishlistIconDefault || "";
      this.wishlistIconActive = this.root.dataset.wishlistIconActive || this.wishlistIconDefault;
      this.wishlistInitTimers = [];
      this.isWishlistReady = false;
      this.swymMoveFrame = null;
      this.swymObserver = null;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onCollectionLinksScroll = this.onCollectionLinksScroll.bind(this);
      this.onWishlistSync = this.onWishlistSync.bind(this);

      this.root.addEventListener("click", this.onClick);
      this.root.addEventListener("change", this.onChange);
      document.addEventListener("keydown", this.onKeydown);
      window.addEventListener("resize", this.onResize);
      window.addEventListener(WISHLIST_SYNC_EVENT, this.onWishlistSync);

      if (this.collectionLinksViewport) {
        this.collectionLinksViewport.addEventListener("scroll", this.onCollectionLinksScroll, {
          passive: true,
        });
      }

      this.bindCollectionLinkImages();
      this.updateCollectionLinksScrollbar();
      this.syncWishlistButtons();
      this.initWishlistButtons();
      this.initSwymWishlistPositioning();
    }

    onClick(event) {
      const wishlistButton = event.target.closest("[data-aw-collection-wishlist-trigger]");
      if (wishlistButton && this.root.contains(wishlistButton)) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleWishlist(wishlistButton);
        return;
      }

      const openButton = event.target.closest("[data-aw-filter-open]");
      if (openButton && this.root.contains(openButton)) {
        this.openFilterDrawer();
        return;
      }

      const closeButton = event.target.closest("[data-aw-filter-close]");
      if (closeButton && this.root.contains(closeButton)) {
        this.closeFilterDrawer();
        return;
      }

      const overlay = event.target.closest("[data-aw-filter-overlay]");
      if (overlay && this.root.contains(overlay)) {
        this.closeFilterDrawer();
        return;
      }

      const toggle = event.target.closest("[data-aw-filter-toggle]");
      if (toggle && this.root.contains(toggle)) {
        const group = toggle.closest("[data-aw-filter-group]");
        if (!group) return;

        this.toggleFilterGroup(group, toggle);
      }
    }

    onChange(event) {
      const select = event.target.closest("[data-aw-sort-select]");
      if (!select || !this.root.contains(select)) return;
      if (select.form) {
        select.form.submit();
      }
    }

    onKeydown(event) {
      if (event.key !== "Escape") return;
      if (!this.root.classList.contains("is-filter-open")) return;
      this.closeFilterDrawer();
    }

    onResize() {
      this.updateCollectionLinksScrollbar();
    }

    onCollectionLinksScroll() {
      this.updateCollectionLinksScrollbar();
    }

    onWishlistSync() {
      this.setWishlistReady(this.isWishlistReady);
      this.syncWishlistButtons();
    }

    getWishlistButtons() {
      return Array.from(this.root.querySelectorAll("[data-aw-collection-wishlist-trigger]"));
    }

    toggleFilterGroup(group, toggle) {
      const content = group.querySelector("[data-aw-filter-content]");
      if (!content) return;

      const isExpanded = toggle.getAttribute("aria-expanded") !== "false";
      toggle.setAttribute("aria-expanded", isExpanded ? "false" : "true");
      content.hidden = isExpanded;
    }

    openFilterDrawer() {
      const drawer = this.root.querySelector("[data-aw-filter-drawer]");
      if (!drawer) return;

      this.root.classList.add("is-filter-open");
      drawer.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
    }

    closeFilterDrawer() {
      const drawer = this.root.querySelector("[data-aw-filter-drawer]");
      if (!drawer) return;

      this.root.classList.remove("is-filter-open");
      drawer.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
    }

    bindCollectionLinkImages() {
      if (!this.collectionLinksViewport) return;

      this.collectionLinksViewport.querySelectorAll("img").forEach((image) => {
        if (image.complete) return;

        image.addEventListener("load", this.onResize, { once: true });
      });
    }

    initWishlistButtons() {
      if (!this.getWishlistButtons().length) {
        return;
      }

      this.setWishlistReady(typeof window._swat?.addToWishList === "function");

      if (this.isWishlistReady) {
        return;
      }

      [150, 500, 1200, 2400].forEach((delay) => {
        const timerId = window.setTimeout(() => {
          this.setWishlistReady(typeof window._swat?.addToWishList === "function");
        }, delay);
        this.wishlistInitTimers.push(timerId);
      });
    }

    getSwymCardMedia(card) {
      if (!(card instanceof HTMLElement)) {
        return null;
      }

      return card.querySelector(
        ".aw-collection-product-card__media, .aw-product-card__media, .aw-product-related-items__media, .aw-product-recommend-items__media"
      );
    }

    syncSwymWishlistContainers() {
      this.root.querySelectorAll("article.aw-product-grid-item").forEach((card) => {
        const container = card.querySelector(".swym-advanced-wishlist-collections-container");
        const media = this.getSwymCardMedia(card);

        if (!container || !media || container.parentElement === media) {
          return;
        }

        media.appendChild(container);
      });
    }

    scheduleSwymWishlistSync() {
      if (this.swymMoveFrame) {
        return;
      }

      this.swymMoveFrame = window.requestAnimationFrame(() => {
        this.swymMoveFrame = null;
        this.syncSwymWishlistContainers();
      });
    }

    initSwymWishlistPositioning() {
      this.scheduleSwymWishlistSync();

      this.swymObserver = new MutationObserver(() => {
        this.scheduleSwymWishlistSync();
      });

      this.swymObserver.observe(this.root, {
        childList: true,
        subtree: true
      });
    }

    getWishlistState() {
      try {
        const raw = window.localStorage.getItem(WISHLIST_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
      } catch (error) {
        return new Set();
      }
    }

    saveWishlistState(nextState) {
      try {
        window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(Array.from(nextState)));
      } catch (error) {
        // Ignore storage failures and keep UI responsive.
      }
    }

    setWishlistState(variantId, isActive) {
      const nextState = this.getWishlistState();
      const key = String(variantId);

      if (isActive) {
        nextState.add(key);
      } else {
        nextState.delete(key);
      }

      this.saveWishlistState(nextState);
      this.syncWishlistButtons(nextState);
      window.dispatchEvent(new CustomEvent(WISHLIST_SYNC_EVENT));
    }

    setWishlistReady(isReady) {
      this.isWishlistReady = !!isReady;
      this.root.classList.toggle("aw-search--wishlist-ready", this.isWishlistReady);

      this.getWishlistButtons().forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) {
          return;
        }

        button.disabled = !this.isWishlistReady;
        button.setAttribute("aria-disabled", this.isWishlistReady ? "false" : "true");
      });
    }

    syncWishlistButtons(nextState = this.getWishlistState()) {
      this.getWishlistButtons().forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) {
          return;
        }

        const variantId = button.dataset.variantId || "";
        const isActive = variantId !== "" && nextState.has(String(variantId));
        const nextLabel = isActive ? "Added to wishlist" : "Add to wishlist";
        const nextIcon = isActive ? this.wishlistIconActive : this.wishlistIconDefault;
        const icon = button.querySelector("[data-aw-collection-wishlist-icon]");
        const text = button.querySelector("[data-aw-collection-wishlist-text]");

        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
        button.setAttribute("aria-label", nextLabel);

        if (icon instanceof HTMLImageElement && nextIcon) {
          icon.setAttribute("src", nextIcon);
        }

        if (text instanceof HTMLElement) {
          text.textContent = nextLabel;
        }
      });
    }

    setWishlistButtonLoading(button, isLoading) {
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      button.classList.toggle("is-loading", isLoading);
      button.disabled = isLoading || !this.isWishlistReady;
      button.setAttribute("aria-disabled", button.disabled ? "true" : "false");
    }

    normalizeUrl(value) {
      if (!value) {
        return "";
      }

      if (value.startsWith("//")) {
        return `${window.location.protocol}${value}`;
      }

      if (value.startsWith("/")) {
        return `${window.location.origin}${value}`;
      }

      return value;
    }

    buildWishlistPayload(button, eventType) {
      const productId = Number(button.dataset.productId || 0);
      const variantId = Number(button.dataset.variantId || 0);

      if (!productId || !variantId) {
        return null;
      }

      const productUrl = this.normalizeUrl(button.dataset.productUrl || "");
      const imageUrl = this.normalizeUrl(button.dataset.productImage || "");

      return {
        et: eventType,
        empi: productId,
        epi: variantId,
        du: productUrl,
        uri: productUrl,
        iu: imageUrl,
        pr: Number(button.dataset.productPrice || 0),
        qty: 1,
        dt: button.dataset.productTitle || "",
        ct: "",
        rc: "default",
        type: "product-variant",
      };
    }

    toggleWishlist(button) {
      if (!(button instanceof HTMLButtonElement) || !this.isWishlistReady || button.disabled) {
        return;
      }

      const variantId = button.dataset.variantId || "";
      const isActive = button.classList.contains("is-active");
      const payload = this.buildWishlistPayload(button, isActive ? 4 : -1);
      const action = isActive ? "removeFromWishList" : "addToWishList";
      const handler = window._swat?.[action];

      if (!payload || typeof handler !== "function" || !variantId) {
        return;
      }

      this.setWishlistButtonLoading(button, true);

      handler.call(
        window._swat,
        payload,
        () => {
          this.setWishlistState(variantId, !isActive);
          this.setWishlistButtonLoading(button, false);
        },
        () => {
          this.setWishlistButtonLoading(button, false);
        }
      );
    }

    updateCollectionLinksScrollbar() {
      if (
        !this.collectionLinksWrap ||
        !this.collectionLinksViewport ||
        !this.collectionLinksScrollbar ||
        !this.collectionLinksScrollbarThumb
      ) {
        return;
      }

      const isCompactViewport = window.innerWidth <= 1099;
      const maxScroll = Math.max(
        0,
        this.collectionLinksViewport.scrollWidth - this.collectionLinksViewport.clientWidth
      );
      const isStatic = !isCompactViewport || maxScroll <= 1;

      this.collectionLinksWrap.classList.toggle("is-static", isStatic);

      if (isStatic) {
        this.collectionLinksScrollbarThumb.style.width = "100%";
        this.collectionLinksScrollbarThumb.style.transform = "translateX(0)";
        return;
      }

      const trackWidth = this.collectionLinksScrollbar.clientWidth;
      const ratio = this.collectionLinksViewport.clientWidth / this.collectionLinksViewport.scrollWidth;
      const thumbWidth = Math.max(trackWidth * ratio, 24);
      const maxThumbX = Math.max(trackWidth - thumbWidth, 0);
      const progress = Math.min(
        Math.max(this.collectionLinksViewport.scrollLeft / maxScroll, 0),
        1
      );
      const thumbX = maxThumbX * progress;

      this.collectionLinksScrollbarThumb.style.width = `${thumbWidth}px`;
      this.collectionLinksScrollbarThumb.style.transform = `translateX(${thumbX}px)`;
    }

    destroy() {
      this.root.removeEventListener("click", this.onClick);
      this.root.removeEventListener("change", this.onChange);
      document.removeEventListener("keydown", this.onKeydown);
      window.removeEventListener("resize", this.onResize);
      window.removeEventListener(WISHLIST_SYNC_EVENT, this.onWishlistSync);
      this.wishlistInitTimers.forEach((timerId) => window.clearTimeout(timerId));

      if (this.swymMoveFrame) {
        window.cancelAnimationFrame(this.swymMoveFrame);
        this.swymMoveFrame = null;
      }

      if (this.swymObserver) {
        this.swymObserver.disconnect();
        this.swymObserver = null;
      }

      if (this.collectionLinksViewport) {
        this.collectionLinksViewport.removeEventListener("scroll", this.onCollectionLinksScroll);
      }

      if (this.root.classList.contains("is-filter-open")) {
        document.body.style.overflow = "";
      }
    }
  }

  const init = (container = document) => {
    container
      .querySelectorAll("[data-aw-search]")
      .forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new AWSearch(root));
    });
  };

  const destroy = (container) => {
    container
      .querySelectorAll("[data-aw-search]")
      .forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  document.addEventListener("DOMContentLoaded", () => init());
  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroy(event.target));
})();

